import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'antd-mobile';
import { useI18n } from '../../../hooks/useI18n';
import { getMyScenarioSessions, type SessionInfo } from '../../../lib/api/conversations';
import bg2 from '@/assets/images/ai/bg2.png';
import err1 from '@/assets/images/ai/err1.png';

// 评分信息接口
interface ScoreInfo {
  overall_score: number;
  max_score: number;
  scoring_time: string;
}

// 会话项接口
interface SessionItem {
  id: string;
  session_token: string;
  scenario_name: string;
  status: number;
  status_name: string;
  round_number: number;
  created_at: string;
  updated_at: string;
  duration?: number;
  score_info?: ScoreInfo;
}

const AiTrainingHistory: React.FC = () => {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const { t } = useI18n();
  const [practiceList, setPracticeList] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scenarioTitle, setScenarioTitle] = useState<string>('');
  const pageSize = 100;
  const navigate = useNavigate();
  // 获取会话数据
  const fetchSessions = async (page: number = 1) => {
    if (!scenarioId) return;
    
    setLoading(true);
    try {
      const response = await getMyScenarioSessions({
        scenario_id: scenarioId,
        page,
        pageSize
      });
      
      if (response.success && response.data) {
        const sessionData = response.data.list.map((session: any) => ({
          id: session.id,
          session_token: session.session_token,
          scenario_name: session.scenario_name,
          status: session.status,
          status_name: session.status_name,
          round_number: session.round_number,
          created_at: session.created_at,
          updated_at: session.updated_at,
          duration: session.duration,
          score_info: session.score_info
        }));
        
        setPracticeList(sessionData);
        setTotal(response.data.total);
        setCurrentPage(response.data.current);
        
        // 提取场景标题
        if (sessionData.length > 0) {
          setScenarioTitle(sessionData[0].scenario_name);
        }
      } else {
        throw new Error(response.errorMessage || '获取数据失败');
      }
    } catch (error) {
      console.error('获取会话列表失败:', error);
      Toast.show({
        content: t('ai.history.fetchRecordsFailed', '获取对话记录失败'),
        icon: 'fail'
      });
    } finally {
      setLoading(false);
    }
  };

  // 格式化分数
  const getScore = (item: SessionItem) => {
    return item.score_info ? item.score_info.overall_score : null;
  };

  // 格式化时长
  const formatDuration = (seconds?: number) => {
    if (!seconds) return t('common.unknown', '未知');
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}${t('common.minute', '分')}${remainingSeconds}${t('common.second', '秒')}`;
  };

  // 格式化时间
  const formatTime = (timeStr: string) => {
    return new Date(timeStr).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 跳转到练习详情页
  const toPracticeDetail = (item: SessionItem) => {
    console.log('跳转到详情页:', item);
    navigate(`/ai-training/analysis/${item.id}`);
  };

  // 再练一次
  const handleRetry = () => {
    console.log('再练一次');
    if (scenarioId) {
      navigate(`/ai-training/detail/${scenarioId}`);
    }
  };

  // 获取状态文本
  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return t('ai.status.chatting', '对话中');
      case 1:
        return t('ai.status.scoring', '评分中');
      case 2:
        return t('ai.status.completed', '已完成');
      case 3:
        return t('ai.status.scoringFailed', '评分失败');
      case 4:
        return t('ai.status.timeout', '对话超时结束');
      case 5:
        return t('ai.status.evaluating', '评价中');
      default:
        return t('ai.status.unknown', '未知状态');
    }
  };

  // 获取状态样式类
  const getStatusClass = (status: number) => {
    switch (status) {
      case 0:
        return 'active'; // 对话中
      case 1:
        return 'scoring'; // 评分中
      case 2:
        return 'passed'; // 已完成
      case 3:
        return 'failed'; // 评分失败
      case 4:
        return 'timeout'; // 对话超时结束
      case 5:
        return 'evaluating'; // 评价中
      default:
        return 'unknown';
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [scenarioId]);

  if (loading) {
    return (
      <motion.div 
        className="min-h-screen flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="text-white text-lg"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{ 
            duration: 1.5, 
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          {t('common.loading', '加载中...')}
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className=" pb-32 overflow-y-auto" 
      style={{
        backgroundImage: `url(${bg2})`,
        backgroundSize: '100%',
        backgroundRepeat: 'no-repeat',
        height: '100vh'
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* 主体内容容器 - 添加最大宽度和居中 */}
      <div className='max-w-4xl mx-auto'>
      {/* 固定标题 */}
      {scenarioTitle && (
        <div 
          className=" mb-4 p-4 bg-white rounded-b-lg sticky top-0 z-40"
        >
          <h2 className="text-lg font-medium text-gray-800 text-center">
            {scenarioTitle}
          </h2>
          <p className="text-sm text-gray-500 text-center mt-1">
            {t('ai.history.practiceHistory', '练习历史')}
          </p>
        </div>
      )}
      {practiceList && practiceList.length > 0 ? (
        <div 
          className="pb-4 pt-2"
        >
          {practiceList.map((item, index) => (
            <motion.div
              key={index}
              className="practice-item bg-white cursor-pointer m-3 p-3"
              onClick={() => toPracticeDetail(item)}
              style={{
                background: 'linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0.5) 100%)',
                boxShadow: '0px 4px 14px 0px rgba(224, 232, 255, 0.65)',
                border: '2px solid #ffffff',
                borderRadius: '8px'
              }}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.5, 
                delay: index * 0.1 + 0.3,
                ease: "easeOut"
              }}
              whileHover={{ 
                scale: 1.02,
                y: -2,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between text-left">
                <div className="flex items-center">
                  {getScore(item) !== null ? (
                    <>
                      <span className="medal-icon mr-2">
                        <i 
                          className="text-purple-600" 
                          style={{ fontSize: '24px' }}
                        >
                          🏆
                        </i>
                      </span>
                      <span 
                        className="text-green-600 font-medium"
                        style={{ fontSize: '16px', lineHeight: '24px' }}
                      >
                        {t('ai.history.score', '得分')}：{getScore(item)?.toFixed(1)}
                      </span>
                    </>
                  ) : (
                    <span 
                      className="text-gray-600 font-medium"
                      style={{ fontSize: '16px', lineHeight: '24px' }}
                    >
                      {t('ai.history.practiceRecord', '练习记录')}
                    </span>
                  )}
                </div>
                <div className="flex items-center">
                  <div
                    className={`flex items-center status-tag ${getStatusClass(item.status)}`}
                    style={{ fontSize: '14px', lineHeight: '22px' }}
                  >
                    <span
                      className="status-icon inline-block w-2 h-2 mr-1 rounded-full"
                    ></span>
                    {getStatusText(item.status)}
                  </div>
                </div>
              </div>

              <div
                className="time-box text-gray-500 text-left pt-3 mt-3"
                style={{
                  fontSize: '14px',
                  lineHeight: '22px',
                  borderTop: '1px solid #ebeef5'
                }}
              >
                <div className="flex justify-between items-center">
                  <span>{t('ai.history.createTime', '创建时间')}：{formatTime(item.created_at)}</span>
                  {item.duration && (
                    <span>{t('ai.history.chatDuration', '时长')}：{formatDuration(item.duration)}</span>
                  )}
                </div>
                {item.score_info && (
                  <div className="mt-2 text-xs text-gray-400">
                    {t('ai.history.scoringTime', '评分时间')}：{formatTime(item.score_info.scoring_time)}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div 
          className="flex flex-col items-center justify-center mt-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <motion.img
            src={err1}
            alt=""
            style={{ width: '80px', height: '80px' }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
          />
          <motion.span 
            className="text-gray-500 mt-2"
            style={{ fontSize: '14px', lineHeight: '24px' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.7 }}
          >
            {t('common.noData', '暂无数据~')}
          </motion.span>
        </motion.div>
      )}
      </div>

      {/* 底部操作按钮 - 撑满宽度但设置最大宽度限制 */}
      <motion.div 
        className="options-box fixed bottom-0 left-0 right-0 w-full flex items-center justify-center bg-white" 
        style={{ paddingBottom: '32px', paddingTop: '12px', borderTop: '2px solid #f5f5f5' }}
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.8, ease: "easeOut" }}
      >
        <div className="w-full max-w-md px-5">
          <motion.div
            className="btn-box text-white text-center cursor-pointer"
            onClick={handleRetry}
            style={{
              width: '100%',
              fontSize: '14px',
              lineHeight: '22px',
              paddingTop: '12px',
              paddingBottom: '12px',
              borderRadius: '39px',
              background: 'linear-gradient(171deg, #a782ff 0%, #828eff 100%), linear-gradient(90deg, #82cbff 0%, #a782ff 100%), rgba(255, 255, 255, 0.55)',
              boxShadow: '0px 0px 14px -1px rgba(31, 6, 90, 0.08)'
            }}
            whileHover={{ 
              scale: 1.05,
              boxShadow: '0px 0px 20px -1px rgba(31, 6, 90, 0.15)'
            }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {t('ai.history.practiceAgain', '再练一次')}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AiTrainingHistory;

// CSS样式（通过styled-components或CSS模块实现）
const styles = `
.status-tag.active {
  color: #409eff;
}

.status-tag.active .status-icon {
  background: #409eff;
}

.status-tag.scoring {
  color: #e6a23c;
}

.status-tag.scoring .status-icon {
  background: #e6a23c;
}

.status-tag.passed {
  color: #15bc83;
}

.status-tag.passed .status-icon {
  background: #15bc83;
}

.status-tag.failed {
  color: #f56c6c;
}

.status-tag.failed .status-icon {
  background: #f56c6c;
}

.status-tag.timeout {
  color: #ff7875;
}

.status-tag.timeout .status-icon {
  background: #ff7875;
}

.status-tag.evaluating {
  color: #722ed1;
}

.status-tag.evaluating .status-icon {
  background: #722ed1;
}

.status-tag.unknown {
  color: #909399;
}

.status-tag.unknown .status-icon {
  background: #909399;
}
`;

// 将样式注入到页面中
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}